import React from 'react';
import { View } from 'react-native';

import { Button } from '@components/Button';
import { Icon } from '@components/Icon';
import { Typography } from '@components/Typography';
import { StyleObj, makeStyles } from '@providers/Theme';

export type TimerInputProps = {
  style?: StyleObj;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange?: (value: number) => void;
};

const useStyles = makeStyles((theme) => ({
  root: () => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  }),
  border: {
    ...theme.mixins.border(theme.palette.primary[50]!, 4),
    borderRadius: 12,
  },
  timer: {
    ...theme.mixins.padding(1, 3),
    ...theme.mixins.border(theme.palette.primary[100]!, 1),
    color: theme.palette.primary[600],
    backgroundColor: 'white',
    borderRadius: 8,
    userSelect: 'none',
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: theme.spacing(1.5),
  },
  button: {
    ...theme.mixins.padding(0),
    borderRadius: 100,
  },
  decrementButton: {
    marginRight: theme.spacing(1.5),
  },
}));

export const TimerInput: React.FC<TimerInputProps> = ({
  style,
  onChange,
  value: valueFromProps,
  min: minFromProps = 0, // 0 minutes by default
  max = 60 * 14, // 14 hours by default
  step = 5, // 5 minutes by default
}) => {
  const styles = useStyles();

  const min = Math.max(minFromProps, 0); // if is negative, stick to 0
  const value = Math.max(Math.min(valueFromProps, max), min); // make sure value is between min and max

  const { hours, minutes } = useClockValue(value);
  const formattedHours = hours ? `${hours}:` : '';
  const formattedMinutes = minutes.toString().padStart(2, '0');

  return (
    <View style={[styles.root, style]}>
      <View style={styles.border}>
        <Typography
          size="4xl"
          weight="semiBold"
          style={
            styles.timer
          }>{`${formattedHours}${formattedMinutes}:00`}</Typography>
      </View>
      <View style={styles.actions}>
        <Button
          style={[styles.button, styles.decrementButton]}
          startIcon={
            <Icon
              name="minus"
              color={(theme) => theme.palette.grey[700]}
              size={36}
            />
          }
          onPress={() => {
            if (value - step >= min) {
              onChange?.(value - step);
            }
          }}
          disabled={value - step < min}
        />

        <Button
          style={styles.button}
          startIcon={
            <Icon
              name="plus"
              color={(theme) => theme.palette.grey[700]}
              size={36}
            />
          }
          onPress={() => {
            if (value + step <= max) {
              onChange?.(value + step);
            }
          }}
          disabled={value + step > max}
        />
      </View>
    </View>
  );
};

function useClockValue(value: number): { hours: number; minutes: number } {
  return {
    hours: Math.floor(value / 60),
    minutes: value % 60,
  };
}
